import type { FileSystemTree } from '@webcontainer/api'
import { cloneDeep, groupBy } from 'lodash'
import { twMerge } from 'tailwind-merge'
import type { NodePath } from '@babel/core'
import * as Babel from '@babel/standalone'
import { parse } from '@babel/parser'
import * as t from '@babel/types'
import MagicString from 'magic-string'
import {
  type JSXElement,
  isJSXAttribute,
  isStringLiteral,
  isJSXExpressionContainer,
} from '@babel/types'
import {
  getFileContentsByPath,
  setFileContentsByPath,
} from '@/lib/file-system-tree'
import type {
  EditorChangeContent,
  EditorMessage,
  EditorMessageData,
  EditorMessageOptions,
} from './editor'
import { getAllFiles, joinPath } from '../utils'
import { kebabToPascalCase } from '../../utils/base'
import type { DeviceView } from '@/contexts/project'

export const REACT_COMPONENT_TAGGER_PACKAGE = 'react-component-tagger'
export const REACT_COMPONENT_TAGGER_VERSION = '^0.0.6'

export const HEIGHT_SCREEN_REGEXP = /!?(h-screen|min-h-screen)/g
export const ANIMATE_REGEXP = /!?animate-([a-zA-Z0-9-]+)(?:-(\w+))?/g
export const ANIMATION_REGEXP =
  /(?<!motion-preset-)(fade|glow|blur|spin|scale|pulse|slide|shake|bounce|rotate|shrink)(?:-\w+)?/g
export const MOTION_PRESET_REGEXP = /!?motion-preset-(\w+)(?:-(\w+))?/g
export const MOTION_DURATION_REGEXP =
  /!?motion-duration-(?:\[)?([\w\.]+)(?:\])?/g

export function rgbaToHex(color: string): string {
  if (color.startsWith('#')) return color
  // Extract numbers from rgb/rgba string
  const values = color.match(/\d+(\.\d+)?/g)
  if (!values) return color

  // Convert RGB values to hex
  const r = Number.parseInt(values[0]).toString(16).padStart(2, '0')
  const g = Number.parseInt(values[1]).toString(16).padStart(2, '0')
  const b = Number.parseInt(values[2]).toString(16).padStart(2, '0')

  // Handle alpha channel if present
  if (values[3]) {
    const a = Math.round(Number.parseFloat(values[3]) * 255)
      .toString(16)
      .padStart(2, '0')
    return `#${r}${g}${b}${a}`
  }

  return `#${r}${g}${b}`
}

export function applySrcChanges(
  magic: MagicString,
  changes: EditorChangeContent[],
  code: string,
  tree: FileSystemTree,
) {
  const srcs = changes.filter(isChangeSrc)
  for (const src of srcs) {
    const old = src.data?.src.old.trim()
    if (old && src.data?.src.new) {
      const decoded = decodeURI(old)
      if (code.includes(decoded)) {
        magic.replace(decoded, src.data.src.new)
      } else if (code.includes(old)) {
        magic.replace(old, src.data.src.new)
      } else if (isTargetSrcImported(tree, src.data, code)) {
        const path = findTargetSrcImportPath(tree, src.data, code)
        if (path && replaceSrcInFile(tree, path, src.data)) continue
      } else {
        const parent = src.data?.component?.parent
        if (parent?.path) {
          const source = getFileContentsByPath(tree, parent.path)
          if (replaceSrcInFile(tree, parent.path, src.data)) {
            continue
          }
          if (isTargetSrcImported(tree, src.data, source)) {
            const path = findTargetSrcImportPath(tree, src.data, source)
            if (path) {
              replaceSrcInFile(tree, path, src.data)
            }
          }
        }
      }
    }
  }
}

export function applyHrefChanges(
  magic: MagicString,
  changes: EditorChangeContent[],
  code: string,
) {
  const hrefs = changes.filter(isChangeHref)
  for (const href of hrefs) {
    if (!href.data) continue

    const old = href.data?.href.old.trim()
    const now = href.data?.href.new.trim()
    const start = href.data.component?.start.index ?? 0
    const slice = code.slice(start, href.data.component?.end.index ?? 0)
    const match = slice.match(/target\s*=\s*["']([^"']+)["']/i)
    if (isHttps(now) && !match) {
      magic.appendLeft(
        start + `<${href.data.component?.name ?? href.data.tag}`.length,
        ` target="_blank" `,
      )
    } else if (now.startsWith('#') && match) {
      const index = match.index ?? 0
      magic.update(start + index, start + index + match[0].length, '')
    }

    if (href.data.component?.name === 'Link') {
      const match = slice.match(/to\s*=\s*["'{]([^"'}]+)["'}]/i)
      if (match) {
        const index = match.index ?? 0
        magic.update(
          start + index,
          start + index + match[0].length,
          `to="${now.startsWith('#') ? now.slice(1) : now}"`,
        )
      }
      continue
    }

    if (isHttps(old) || (old.startsWith('#') && old.length > 1)) {
      magic.replace(old, now)
    } else if (old === '#') {
      const index = slice.indexOf('#')
      if (index !== -1) {
        magic.update(start + index, start + index + 1, now)
      }
    }
  }
}

export function applyTextChanges(
  magic: MagicString,
  changes: EditorChangeContent[],
  code: string,
  tree: FileSystemTree,
) {
  const texts = changes.filter(isChangeText)

  for (const text of texts) {
    if (!text.data?.text.old.trim() || !text.data.component) continue

    // in current file
    const snip = code.slice(
      text.data.component.start.index,
      text.data.component.end.index,
    )
    const list = whereCodeHasText(snip, text.data.text.old)
    if (list.length === 1) {
      const start = text.data.component.start.index + list[0].start
      const end = text.data.component.start.index + list[0].end
      magic.update(start, end, text.data?.text.new.trim())

      // Special case: If old text had trailing newlines that are being removed,
      // also check for and remove any <br /> tags that might follow
      if (
        text.data.text.old.endsWith('\n\n') &&
        !text.data.text.new.endsWith('\n')
      ) {
        try {
          const afterTextEnd = text.data.component.start.index + list[0].end

          const remainingCode = code.slice(
            afterTextEnd,
            text.data.component.end.index,
          )

          // Look for <br /> or <br/> tag anywhere in the remaining code
          const brMatch = remainingCode.match(/(<br\s*\/?>)/i)
          if (brMatch) {
            const brStart = afterTextEnd + (brMatch.index || 0)
            const brEnd = brStart + brMatch[0].length
            magic.update(brStart, brEnd, '')

            // Also check for empty fragments that might follow
            const afterBr = code.slice(brEnd, text.data.component.end.index)
            const emptyFragmentMatch = afterBr.match(/(<><\/>)/)
            if (emptyFragmentMatch) {
              const fragStart = brEnd + (emptyFragmentMatch.index || 0)
              const fragEnd = fragStart + emptyFragmentMatch[0].length
              magic.update(fragStart, fragEnd, '')
            }
          } else {
            // Try looking in the entire component
            const componentCode = code.slice(
              text.data.component.start.index,
              text.data.component.end.index,
            )
            const brInComponent = componentCode.match(/(<br\s*\/?>)/gi)
            if (brInComponent) {
              // Remove all br tags in the component
              brInComponent.forEach((br) => {
                const brIndex = componentCode.indexOf(br)
                if (brIndex !== -1) {
                  const absoluteStart =
                    text.data!.component!.start.index + brIndex
                  const absoluteEnd = absoluteStart + br.length
                  magic.update(absoluteStart, absoluteEnd, '')
                }
              })
            }
          }
        } catch (error) {
          console.error('Error in br tag detection:', error)
        }
      }
    } else {
      const list = whereCodeHasText(code, text.data.text.old)
      if (list.length === 1) {
        magic.replace(text.data?.text.old, text.data?.text.new.trim())
        continue
      }
    }
    // in other files
    if (isTargetTextImported(tree, text.data, code)) {
      const path = findTargetTextImportPath(tree, text.data, code)
      if (path && replaceTextInFile(tree, path, text.data)) {
        continue
      }
    }

    const parent = text.data.component?.parent
    if (parent?.path) {
      const source = getFileContentsByPath(tree, parent.path)
      const founds = whereCodeHasText(source, text.data.text.old)
      if (
        founds.length === 1 &&
        replaceTextInFile(tree, parent.path, text.data)
      ) {
        continue
      }

      if (isTargetTextImported(tree, text.data, source)) {
        const path = findTargetTextImportPath(tree, text.data, source)
        if (path) {
          replaceTextInFile(tree, path, text.data)
          continue
        }
      }
    }

    // Fallback: search all source files for a unique text match.
    // Handles array/map patterns where text is defined in a different file.
    const allFiles = getAllFiles(tree)
    for (const file of allFiles) {
      if (whereCodeHasText(file.content, text.data.text.old).length === 1) {
        replaceTextInFile(tree, file.path, text.data)
        break
      }
    }
  }
}

export function replaceSrcInFile(
  tree: FileSystemTree,
  path: string,
  data: EditorMessageData,
) {
  const source = getFileContentsByPath(tree, path)
  if (!source) return false
  const magic = new MagicString(source)
  const decoded = decodeURI(data.src.old)
  if (source.includes(decoded)) {
    magic.replace(decoded, data.src.new)
  } else if (source.includes(data.src.old)) {
    magic.replace(data.src.old, data.src.new)
  }
  if (magic.hasChanged()) {
    setFileContentsByPath(tree, path, magic.toString())
    return true
  }
  return false
}

export function replaceTextInFile(
  tree: FileSystemTree,
  path: string,
  data: EditorMessageData,
) {
  const source = getFileContentsByPath(tree, path)
  if (!source) return false
  const magic = new MagicString(source)
  magic.replace(data.text.old, data.text.new.trim())
  setFileContentsByPath(tree, path, magic.toString())
  return true
}

export function applyClassChanges(
  magic: MagicString,
  changes: EditorChangeContent[],
) {
  const classes = changes.filter(isChangeClass)
  return (path: NodePath<JSXElement>) => {
    const index = classes.findIndex(
      (klass) => klass.data?.component?.start.index === path.node.start,
    )
    if (index === -1) return
    const className = path.node.openingElement.attributes.find(
      (attr) => isJSXAttribute(attr) && attr.name.name === 'className',
    )
    if (isJSXAttribute(className)) {
      if (
        isStringLiteral(className.value) &&
        className.value.start &&
        className.value.end &&
        classes[index].data?.tailwind
      ) {
        magic.update(
          className.value.start,
          className.value.end,
          `"${classes[index].data?.tailwind}"`,
        )
      } else if (
        isJSXExpressionContainer(className.value) &&
        (isStringLiteral(className.value.expression) ||
          className.value.expression.type === 'TemplateLiteral') &&
        className.value.expression.start &&
        className.value.expression.end &&
        classes[index].data?.tailwind
      ) {
        magic.update(
          className.value.expression.start,
          className.value.expression.end,
          `"${classes[index].data?.tailwind}"`,
        )
      }
    }
    classes.splice(index, 1)
  }
}

export function applyComponentChanges(
  magic: MagicString,
  changes: EditorChangeContent[],
) {
  const componentChanges = changes.filter(isChangePayload)
  return (path: NodePath<JSXElement>) => {
    const index = componentChanges.findIndex(
      (component) => component.data?.component?.start.index === path.node.start,
    )

    if (index === -1) return
    const matchChange = componentChanges[index]
    if (!matchChange) return
    const oldName = matchChange.data?.payload!.old.componentName
    const newName = matchChange.data?.payload!.new.componentName

    const openingElement = path.node.openingElement
    const closingElement = path.node.closingElement

    if (
      t.isJSXIdentifier(openingElement.name) &&
      openingElement.name.name === oldName &&
      openingElement.name.start != null &&
      openingElement.name.end != null &&
      newName
    ) {
      magic.overwrite(
        openingElement.name.start,
        openingElement.name.end,
        kebabToPascalCase(newName),
      )
    }

    // 如果有对应的闭合标签，也改名（非自闭合标签情况）
    if (
      closingElement &&
      t.isJSXIdentifier(closingElement.name) &&
      closingElement.name.name === oldName &&
      closingElement.name.start != null &&
      closingElement.name.end != null &&
      newName
    ) {
      magic.overwrite(
        closingElement.name.start,
        closingElement.name.end,
        kebabToPascalCase(newName),
      )
    }

    componentChanges.splice(index, 1) // 避免重复处理相同节点
  }
}

export function applyComponentImportChanges(
  magic: MagicString,
  changes: EditorChangeContent[],
) {
  const componentChanges = changes.filter(isChangePayload)

  return (path: NodePath<t.ImportDeclaration>) => {
    const specifiers = path.node.specifiers

    // 当前 import 中所有已导入的名字
    const importedNames = specifiers
      .filter((s) => t.isImportSpecifier(s) || t.isImportDefaultSpecifier(s))
      .map((s) => s.local.name)

    const newNamesToInsert: string[] = []

    for (const change of componentChanges) {
      const oldName = change.data?.payload?.old.componentName
      const newNameRaw = change.data?.payload?.new.componentName
      if (!oldName || !newNameRaw) continue

      const newName = kebabToPascalCase(newNameRaw)

      // OldName 一定存在，只处理 newName 尚未导入的情况
      const hasOld = importedNames.includes(oldName)
      const hasNew = importedNames.includes(newName)

      if (hasOld && !hasNew) {
        newNamesToInsert.push(newName)
      }
    }

    if (newNamesToInsert.length === 0) return

    // 找到花括号 import 的闭合前插入位置，例如：
    // import { A, B } from '...'
    // 插入在 `B` 的 `end` 后面
    const lastImportSpecifier = specifiers
      .filter((s) => t.isImportSpecifier(s)) // 仅处理命名导入
      .at(-1)

    if (lastImportSpecifier && lastImportSpecifier.end != null) {
      const insertText = `, ${newNamesToInsert.join(', ')}`
      magic.appendLeft(lastImportSpecifier.end, insertText)
    }
  }
}

export function applyIFrameChanges(
  magic: MagicString,
  changes: EditorChangeContent[],
  code: string,
  tree: FileSystemTree,
) {
  const embedChanges = changes.filter(isChangeEmbedUrl)
  for (const change of embedChanges) {
    if (!change.data?.embedUrl) continue
    const start = change.data.component?.start?.index ?? -1
    const match = { start: -1, end: -1 }

    Babel.transform(code, {
      presets: ['react', ['typescript', { isTSX: true, allExtensions: true }]],
      sourceType: 'module',
      plugins: [
        () => ({
          visitor: {
            JSXElement: (path: NodePath<JSXElement>) => {
              // Check if this is the component we're looking for
              if (path.node.openingElement.start !== start) return

              // Look for iframe elements
              const iframeElement = path.node.children.find((child) => {
                return (
                  child.type === 'JSXElement' &&
                  child.openingElement.name.type === 'JSXIdentifier' &&
                  child.openingElement.name.name === 'iframe'
                )
              }) as JSXElement | undefined
              if (iframeElement) {
                const srcAttribute =
                  iframeElement.openingElement.attributes.find(
                    (attr): attr is t.JSXAttribute =>
                      attr.type === 'JSXAttribute' &&
                      attr.name.type === 'JSXIdentifier' &&
                      attr.name.name === 'src',
                  )
                if (srcAttribute) {
                  match.start = srcAttribute.start ?? -1
                  match.end = srcAttribute.end ?? -1
                }
              }
            },
          },
        }),
      ],
    })

    if (change.type === 'editor.change.embedUrl') {
      if (match.start > -1) {
        magic.update(match.start, match.end, `src="${change.data.embedUrl}"`)
      }
    }
  }
}

export function applyButtonChanges(
  magic: MagicString,
  changes: EditorChangeContent[],
  code: string,
  tree: FileSystemTree,
) {
  const buttons = changes.filter(isChangeButton)
  for (const button of buttons) {
    if (!button.data?.link) continue
    const start = button.data.component?.start?.index ?? -1
    const match = { start: -1, end: -1 }
    const index = `<${button.data.component?.name ?? 'button'}`.length

    Babel.transform(code, {
      presets: ['react', ['typescript', { isTSX: true, allExtensions: true }]],
      sourceType: 'module',
      plugins: [
        () => ({
          visitor: {
            JSXElement: (path: NodePath<JSXElement>) => {
              if (path.node.start !== start) return
              const onClick = path.node.openingElement.attributes.find(
                (attr) => isJSXAttribute(attr) && attr.name.name === 'onClick',
              )
              if (onClick) {
                match.start = onClick.start ?? 0
                match.end = onClick.end ?? 0
              }
            },
          },
        }),
      ],
    })

    if (button.type === 'editor.change.button.openurl') {
      if (match.start > -1) {
        magic.update(
          match.start,
          match.end,
          `onClick={() => { window.open("${button.data.link}", "_blank"); }}`,
        )
      } else if (start > -1) {
        magic.appendLeft(
          start + index,
          ` onClick={() => { window.open("${button.data.link}", "_blank"); }} `,
        )
      }
    } else if (button.type === 'editor.change.button.navigate') {
      if (match.start > -1) {
        magic.update(
          match.start,
          match.end,
          `onClick={() => { location.hash = "#${button.data.link}"; }}`,
        )
      } else if (start > -1) {
        magic.appendLeft(
          start + index,
          ` onClick={() => { location.hash = "#${button.data.link}"; }} `,
        )
      }
    } else if (
      typeof button.data.link === 'object' &&
      button.type === 'editor.change.button.scroll'
    ) {
      const to = button.data.link
      const id = to.domid || to.id
      const idAttrCode = ` id="${id}" `
      const isSameFile =
        Boolean(button.data.component?.path) &&
        button.data.component?.path === to.component?.path
      if (!to.domid) {
        const tag = `<${to.component?.name ?? to.tag}`
        const src = getFileContentsByPath(tree, to.component?.path ?? '')
        const idx = to.component?.start.index ?? 0
        if (isSameFile) {
          magic.appendLeft(idx + tag.length, idAttrCode)
        } else if (src) {
          const mgc = new MagicString(src)
          mgc.prependLeft(idx + tag.length, idAttrCode)
          setFileContentsByPath(tree, to.component?.path ?? '', mgc.toString())
        }
      }
      if (match.start > -1) {
        magic.update(
          match.start,
          match.end,
          `onClick={() => { document.getElementById("${id}")?.scrollIntoView({ block: 'center', behavior: 'smooth' }); }}`,
        )
      } else if (start > -1) {
        magic.appendLeft(
          start + index,
          ` onClick={() => { document.getElementById("${id}")?.scrollIntoView({ block: 'center', behavior: 'smooth' }); }} `,
        )
      }
    }
  }
}

export function applyRemoveChanges(
  magic: MagicString,
  changes: EditorChangeContent | EditorChangeContent[],
) {
  const removes = (Array.isArray(changes) ? changes : [changes]).filter(
    isChangeRemove,
  )

  for (const remove of removes) {
    const component = remove.data?.component
    if (component?.map) {
      magic.update(component.map.start.index, component.map.end.index, 'null')
    } else if (component?.start && component?.end) {
      // For self-closing tags like <br />, completely remove them instead of replacing with fragment
      const elementText = magic.original.slice(
        component.start.index,
        component.end.index,
      )

      if (elementText.includes('<br') && elementText.includes('/>')) {
        magic.update(component.start.index, component.end.index, '')
      } else {
        magic.update(component.start.index, component.end.index, '<></>')
      }
    }
  }
}

export function newFilesSystemTree(
  tree: FileSystemTree,
  changes: EditorChangeContent[],
  options?: EditorMessageOptions,
) {
  const repo = cloneDeep(tree)
  const todos = groupBy(changes, 'data.component.path')

  for (const [path, changes] of Object.entries(todos)) {
    const code = getFileContentsByPath(repo, path)
    const list = mergeChanges(changes, options)
    const magic = new MagicString(code)
    applySrcChanges(magic, list, code, repo)
    applyHrefChanges(magic, list, code)
    applyTextChanges(magic, list, code, repo)
    applyRemoveChanges(magic, list)
    applyButtonChanges(magic, list, code, repo)
    applyIFrameChanges(magic, list, code, repo)

    Babel.transform(code, {
      presets: ['react', ['typescript', { isTSX: true, allExtensions: true }]],
      sourceType: 'module',
      plugins: [
        () => ({
          visitor: {
            JSXElement(path: NodePath<t.JSXElement>) {
              // 多个访问器复合调用
              applyClassChanges(magic, list)(path)
              // 变更组件名称
              applyComponentChanges(magic, list)(path)
            },
            ImportDeclaration(path: NodePath<t.ImportDeclaration>) {
              // 变更组件 import
              applyComponentImportChanges(magic, list)(path)
            },
          },
        }),
      ],
    })

    setFileContentsByPath(repo, path, magic.toString())
  }
  return repo
}

export function isTargetHrefInOwnFile(
  tree: FileSystemTree,
  data: EditorMessageData,
) {
  if (!tree || !data?.component?.path) return false
  const code = getFileContentsByPath(tree, data.component.path)
  const href = data.href.old.trim()
  const path = href.startsWith('#') ? href.slice(1) : href
  return (
    (isHttps(href) || href.startsWith('#')) &&
    (code.includes(href) || code.includes(path)) &&
    ['a', 'motion.a', 'Link'].includes(data.component?.name ?? '')
  )
}

export function isTargetSrcEditable(
  tree: FileSystemTree,
  data: EditorMessageData,
) {
  if (!tree || !data?.component?.path) return false
  if (!['image', 'video'].includes(data.type)) return false
  if (!data?.src?.old) return false

  const code = getFileContentsByPath(tree, data.component.path)
  if (
    code.includes(data.src.old) ||
    code.includes(decodeURI(data.src.old)) ||
    isTargetSrcImported(tree, data, code)
  ) {
    return true
  }

  const parent = data.component?.parent
  if (parent?.path) {
    const source = getFileContentsByPath(tree, parent.path)
    if (
      source.includes(data.src.old) ||
      source.includes(decodeURI(data.src.old)) ||
      isTargetSrcImported(tree, data, source)
    ) {
      return true
    }
  }

  return false
}

export function isTargetTextEditable(
  tree: FileSystemTree,
  data: EditorMessageData,
) {
  if (
    !tree ||
    !data?.component?.path ||
    !data.text.old.trim() ||
    data.type !== 'text'
  ) {
    return false
  }
  const code = getFileContentsByPath(tree, data.component.path)
  const snip = code.slice(data.component.start.index, data.component.end.index)
  const text = data.text.old.trim()
  if (
    whereCodeHasText(snip, text).length === 1 ||
    whereCodeHasText(code, text).length === 1 ||
    isTargetTextImported(tree, data, code)
  ) {
    return true
  }

  const parent = data.component?.parent
  if (parent?.path) {
    const source = getFileContentsByPath(tree, parent.path)
    const founds = whereCodeHasText(source, data.text.old.trim())
    if (founds.length === 1 || isTargetTextImported(tree, data, source)) {
      return true
    }
  }

  // Fallback: search all source files in the project tree.
  // Handles cases where component path points to a child component (e.g. Accordion)
  // but the actual text is defined in a different file (e.g. in a data array).
  const allFiles = getAllFiles(tree)
  let globalMatchCount = 0
  for (const file of allFiles) {
    globalMatchCount += whereCodeHasText(file.content, text).length
    if (globalMatchCount > 1) {
      break
    }
  }
  if (globalMatchCount === 1) {
    return true
  }

  return false
}

export function isTargetSrcImported(
  tree: FileSystemTree,
  data: EditorMessageData,
  code: string,
) {
  return Boolean(
    getTargetImportPaths(tree, data, code)
      .map((path) => getFileContentsByPath(tree, path))
      .find(
        (code) =>
          code.includes(data.src.old.trim()) ||
          code.includes(decodeURI(data.src.old.trim())),
      ),
  )
}

export function findTargetSrcImportPath(
  tree: FileSystemTree,
  data: EditorMessageData,
  code: string,
) {
  return getTargetImportPaths(tree, data, code).find((path) => {
    const source = getFileContentsByPath(tree, path)
    return (
      source.includes(data.src.old.trim()) ||
      source.includes(decodeURI(data.src.old.trim()))
    )
  })
}

export function isTargetTextImported(
  tree: FileSystemTree,
  data: EditorMessageData,
  code: string,
) {
  if (!code || !data.component?.path || !data.text.old.trim()) return false

  return (
    getTargetImportPaths(tree, data, code).flatMap((path) =>
      whereCodeHasText(getFileContentsByPath(tree, path), data.text.old.trim()),
    ).length === 1
  )
}

export function findTargetTextImportPath(
  tree: FileSystemTree,
  data: EditorMessageData,
  code: string,
) {
  return getTargetImportPaths(tree, data, code).find((path) => {
    const source = getFileContentsByPath(tree, path)
    const founds = whereCodeHasText(source, data.text.old.trim())
    return founds.length === 1
  })
}

export function getTargetImportPaths(
  tree: FileSystemTree,
  data: EditorMessageData,
  code: string,
) {
  if (!code || !data.component?.path) return []

  const ast = parse(code, {
    sourceType: 'module',
    plugins: ['jsx', 'typescript'],
  })

  return ast.program.body
    .filter(
      (node) =>
        t.isImportDeclaration(node) &&
        ['./', '../'].some((prefix) => node.source.value.startsWith(prefix)),
    )
    .map((node) =>
      joinPath(
        data.component?.path,
        (node as t.ImportDeclaration).source.value,
      ),
    )
    .flatMap((path) => [
      `${path}.tsx`,
      `${path}.ts`,
      `${path}.jsx`,
      `${path}.js`,
    ])
    .filter((path) => Boolean(getFileContentsByPath(tree, path)))
}

export function whereCodeHasText(code: string, text: string) {
  const indexes: number[] = []
  let trim = ''

  for (let i = 0; i < code.length; i++) {
    const char = code[i]
    if (/\s/.test(char)) {
      if (trim.length === 0 || trim[trim.length - 1] !== ' ') {
        trim += ' '
        indexes.push(i)
      }
    } else {
      trim += char
      indexes.push(i)
    }
  }

  const source = trim.trim()
  const target = text.replace(/\s+/g, ' ').trim()

  const results = []
  for (let cursor = 0; cursor < source.length; ) {
    const index = source.indexOf(target, cursor)
    if (index === -1) break

    const start = indexes[index]
    const end = indexes[index + target.length - 1] + 1

    results.push({
      start,
      end,
      text,
      code: code.slice(start, end),
    })

    cursor = index + 1
  }

  return results
}

export function findChangeIndex(
  changes: EditorChangeContent[],
  message: EditorChangeContent,
) {
  const { type, data } = message
  if (type === 'editor.change.text') {
    return changes.findIndex(
      (change) =>
        change.type === type &&
        change.data?.id === data?.id &&
        change.data?.child === data?.child,
    )
  }

  if (type === 'editor.change.remove') {
    return changes.findLastIndex(
      (change) =>
        change.type === type &&
        change.data?.component?.path === data?.component?.path &&
        change.data?.component?.start.index === data?.component?.start.index,
    )
  }

  if (type.startsWith('editor.change.button')) {
    return changes.findIndex(
      (change) =>
        change.type.startsWith('editor.change.button') &&
        change.data?.id === data?.id,
    )
  }

  if (type.startsWith('editor.change.motion')) {
    return changes.findIndex(
      (change) =>
        change.type.startsWith('editor.change.motion') &&
        change.data?.id === data?.id &&
        change.data?.motion?.name,
    )
  }

  return changes.findIndex(
    (change) => change.type === type && change.data?.id === data?.id,
  )
}

export function isEditorSelect(change: EditorMessage) {
  return change.type === 'editor.select'
}

export function isHttps(url: string | undefined) {
  return Boolean(url?.trim().startsWith('https://'))
}

export function formatUrl(url: string) {
  if (isHttps(url) || url.startsWith('#')) return url
  if (/^(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})(\/[^\s]*)?$/.test(url)) {
    return `https://${url}`
  }
  return url
}

export function isNotChange(change: EditorChangeContent) {
  return !change.type?.startsWith('editor.change')
}

export function isChangeSrc(change: EditorChangeContent) {
  return change.type === 'editor.change.src'
}

export function isChangePayload(change: EditorChangeContent) {
  return change.type === 'editor.change.payload'
}

export function isChangeHref(change: EditorChangeContent) {
  return change.type === 'editor.change.href'
}

export function isChangeText(change: EditorChangeContent) {
  return change.type === 'editor.change.text'
}

export function isChangeStyle(change: EditorChangeContent) {
  return change.type?.startsWith('editor.change.style')
}

export function isChangeClass(change: EditorChangeContent) {
  return change.type === 'editor.change.class'
}

export function isChangeButton(change: EditorChangeContent) {
  return change.type?.startsWith('editor.change.button')
}

export function isChangeHeight(change: EditorChangeContent) {
  return change.type === 'editor.change.style.height'
}

export function isChangeMotion(change: EditorChangeContent) {
  return change.type?.startsWith('editor.change.motion')
}

export function isChangeRemove(change: EditorChangeContent) {
  return change.type === 'editor.change.remove'
}

export function isChangeRemoveMapCall(change: EditorChangeContent) {
  return (
    change.type === 'editor.change.remove' &&
    Boolean(change.data?.component?.map)
  )
}

export function isChangeEmbedUrl(change: EditorChangeContent) {
  return change.type?.startsWith('editor.change.embedUrl')
}

export function isChangeRemoveElement(change: EditorChangeContent) {
  return change.type === 'editor.change.remove' && !change.data?.component?.map
}

export function removeMotionIfExists(classes: string, exists: boolean) {
  return exists
    ? classes
        .replace(ANIMATE_REGEXP, '')
        .replace(ANIMATION_REGEXP, '')
        .replace(MOTION_PRESET_REGEXP, '')
        .replace(MOTION_DURATION_REGEXP, '')
    : classes
}

export function removeHeightIfExists(classes: string, exists: boolean) {
  return exists ? classes.replace(HEIGHT_SCREEN_REGEXP, '') : classes
}

export function mergeChanges(
  changes: EditorChangeContent[],
  options?: EditorMessageOptions,
) {
  // changes are in the same file
  const groups = groupBy(changes, 'data.component.start.index')
  const result: EditorChangeContent[] = []
  for (const [_, changes] of Object.entries(groups)) {
    const rm = changes.find(isChangeRemove)
    if (rm) {
      result.push(rm)
      continue
    }
    const styles = changes.reduce((styles, change) => {
      if (isChangeStyle(change) || isChangeMotion(change)) {
        styles.push(change)
      } else {
        result.push(change)
      }
      return styles
    }, [] as EditorChangeContent[])

    const motion = styles.find(isChangeMotion)
    const height = styles.find(isChangeHeight)
    const change = styles[0]
    const classes = mergeStyles(styles, options)
    if (classes || height || motion) {
      result.push({
        type: 'editor.change.class',
        data: {
          ...change.data!,
          tailwind: twMerge(
            removeMotionIfExists(
              removeHeightIfExists(
                change.data?.component?.class ?? '',
                Boolean(height),
              ),
              Boolean(motion),
            ),
            classes,
          ).trim(),
        },
      })
    }
  }
  return result
}

// 单独对 fontSize 处理，web 和 desktop 兼容
function responsiveFontSizeClass(changes: EditorChangeContent[]): string {
  let mobileFontSize: string | null = null
  let desktopFontSize: string | null = null
  let originalTextClass = ''

  for (const change of changes) {
    if (!change.type?.includes('fontSize')) continue
    const property = change.type.split('.').pop()
    const className = change.data?.className || ''
    const fontSize = change.data?.style?.fontSize

    if (property === 'fontSize__mobile' && fontSize) {
      mobileFontSize = fontSize
      const match = className.match(
        /\btext-(xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl|7xl|8xl|9xl|\[\d+(px|rem|em)?\])\b/,
      )
      if (match) originalTextClass = match[0]
    }

    if (property === 'fontSize__desktop' && fontSize) {
      desktopFontSize = fontSize
    }
  }

  let result = ''

  // fallback：保留原样的 sm:!text-xxx 作为 desktop 的 reset（mobile-only 时使用）
  const fallback = originalTextClass ? `sm:!${originalTextClass} ` : ''

  if (mobileFontSize && desktopFontSize) {
    result = `!text-[${mobileFontSize}] sm:!text-[${desktopFontSize}]`
  } else if (mobileFontSize) {
    result = `${fallback}!text-[${mobileFontSize}]`
  } else if (desktopFontSize) {
    result = `sm:!text-[${desktopFontSize}]`
  }

  return result.trim()
}

export function mergeStyles(
  changes: EditorChangeContent[],
  options?: EditorMessageOptions,
) {
  // fontSize 需要兼容 web/mobile，需要单独处理
  const fontSizeChanges = changes.filter((change) => {
    if (!isChangeStyle(change)) return false
    const property = change.type.split('.').pop()
    if (!property) return false
    return ['fontSize', 'fontSize__mobile', 'fontSize__desktop'].includes(
      property,
    )
  })

  const fontSizeClass = responsiveFontSizeClass(fontSizeChanges)

  // all styles are applied to the same element
  return changes.reduce((result, change) => {
    if (isChangeStyle(change)) {
      const property = change.type.split('.').pop()
      switch (property) {
        case 'color':
          return `${result} text-[${change.data?.style?.color}]`
        case 'backgroundColor':
          return `${result} !bg-[${change.data?.style?.backgroundColor}]`
        case 'height':
          return `${result} !h-[${change.data?.style?.height}]`
        case 'display':
          return `${result} !${change.data?.style?.display}`
        case 'objectFit':
          return `${result} object-${change.data?.style?.objectFit}`
        case 'lineHeight':
          return `${result} !leading-[${change.data?.style?.lineHeight}]`
        case 'fontStyle':
          return `${result} ${change.data?.style?.fontStyle === 'italic' ? 'italic' : 'not-italic'}`
        case 'fontWeight':
          return `${result} ${Number.parseInt(change.data?.style?.fontWeight ?? '0') >= 700 ? 'font-bold' : ''}`
        case 'fontFamily':
          return `${result} font-['${(change.data?.style?.fontFamily ?? '').replace(/\s+/g, '_')}']`
        case 'textAlign':
          return `${result} text-${change.data?.style?.textAlign}`
        case 'textDecoration':
          return `${result} ${['underline', 'line-through'].includes(change.data?.style?.textDecoration ?? '') ? change.data?.style?.textDecoration : 'no-underline'}`
        case 'marginLeft':
          return `${result} ml-[${change.data?.style?.marginLeft}]`
        case 'marginRight':
          return `${result} mr-[${change.data?.style?.marginRight}]`
        case 'marginTop':
          return `${result} mt-[${change.data?.style?.marginTop}]`
        case 'marginBottom':
          return `${result} mb-[${change.data?.style?.marginBottom}]`
        case 'paddingLeft':
          return `${result} pl-[${change.data?.style?.paddingLeft}]`
        case 'paddingRight':
          return `${result} pr-[${change.data?.style?.paddingRight}]`
        case 'paddingTop':
          return `${result} !pt-[${change.data?.style?.paddingTop}]`
        case 'paddingBottom':
          return `${result} !pb-[${change.data?.style?.paddingBottom}]`
      }
    }

    if (isChangeMotion(change)) {
      const motion = change.data?.motion
      if (!motion) return result
      if (motion.name === 'none') return removeMotionIfExists(result, true)
      const preset =
        motion.name === 'confetti'
          ? 'motion-preset-confetti'
          : `${motion.direction ? `!motion-preset-${motion.name}-${motion.direction}` : `!motion-preset-${motion.name}`}`
      const duration = motion.duration
        ? motion.duration.endsWith('s')
          ? `motion-duration-[${motion.duration}]`
          : `motion-duration-${motion.duration}`
        : null
      return duration
        ? `${result} ${preset} !${duration}`
        : `${result} ${preset}`
    }

    return result
  }, fontSizeClass || '')
}

export function hasReactComponentTagger(tree: FileSystemTree) {
  if (!tree) return false
  const content = getFileContentsByPath(tree, 'vite.config.js')
  if (!content) return false
  return content.includes(REACT_COMPONENT_TAGGER_PACKAGE)
}

export function addReactComponentTagger(tree: FileSystemTree) {
  if (!tree) return
  upgrade(tree)
  vitecfg(tree)
}

function upgrade(tree: FileSystemTree) {
  const code = getFileContentsByPath(tree, 'package.json')
  if (!code) return
  try {
    const pkg = JSON.parse(code) as {
      devDependencies: Record<string, string>
    }
    if (!pkg.devDependencies) {
      pkg.devDependencies = {}
    }
    pkg.devDependencies[REACT_COMPONENT_TAGGER_PACKAGE] =
      REACT_COMPONENT_TAGGER_VERSION
    setFileContentsByPath(tree, 'package.json', JSON.stringify(pkg, null, 2))
  } catch (error) {
    console.error('react-component-tagger: upgrade failed', error)
  }
}

function vitecfg(tree: FileSystemTree) {
  const code = getFileContentsByPath(tree, 'vite.config.js')
  if (!code || code.includes(REACT_COMPONENT_TAGGER_PACKAGE)) return
  try {
    const config = newViteConfig(code)
    if (!config) return
    setFileContentsByPath(tree, 'vite.config.js', config)
  } catch (error) {
    console.error('react-component-tagger: vitecfg failed', error)
  }
}

export function newViteConfig(code: string) {
  if (code.includes(REACT_COMPONENT_TAGGER_PACKAGE)) return false

  const result = Babel.transform(code, {
    sourceType: 'module',
    plugins: [
      () => {
        return {
          visitor: {
            Program(path: NodePath<t.Program>) {
              let lastImportIndex = -1
              path.node.body.forEach((node: t.Node, index: number) => {
                if (node.type === 'ImportDeclaration') {
                  lastImportIndex = index
                }
              })

              const importDeclaration = t.importDeclaration(
                [
                  t.importSpecifier(
                    t.identifier('reactComponentTagger'),
                    t.identifier('reactComponentTagger'),
                  ),
                ],
                t.stringLiteral('react-component-tagger'),
              )

              // Insert import after last import
              path.node.body.splice(lastImportIndex + 1, 0, importDeclaration)

              // Add empty statement for spacing
              const emptyStatement = t.emptyStatement()
              path.node.body.splice(lastImportIndex + 2, 0, emptyStatement)
            },

            ArrayExpression(path: NodePath<t.ArrayExpression>) {
              if (
                path.parent.type === 'ObjectProperty' &&
                path.parent.key.type === 'Identifier' &&
                path.parent.key.name === 'plugins'
              ) {
                const plugin = t.callExpression(
                  t.identifier('reactComponentTagger'),
                  [],
                )
                path.node.elements.push(plugin)
              }
            },
          },
        }
      },
    ],
  })

  return result?.code?.replace(/;\n;/, ';\n')
}

export function removeProductPaymentLink(tree: FileSystemTree) {
  // remove any product payment link in the file system tree any files
  const files = getAllFiles(tree)
  for (const file of files) {
    const code = getFileContentsByPath(tree, file.path)
    if (!code) continue
    const magic = new MagicString(code)
    // if any url contains /payment-link replace it with empty string
    magic.replace(
      /https:\/\/[a-zA-Z0-9\._\-\/]+\/projects\/[a-zA-Z0-9]+\/products\/[a-zA-Z0-9]+\/payment-link/g,
      `${process.env.NEXT_PUBLIC_APP_URL}/payment-link-error`,
    )
    setFileContentsByPath(tree, file.path, magic.toString())
  }
}
